import { defineStore, storeToRefs } from 'pinia';
import { computed } from 'vue';
import {
  useGetV7DatasStatCommonCategories,
  useGetV7DatasStat,
} from '@/api-client/metav6/queries/data-stat-information-controller/data-stat-information-controller';
import { VIEW_MODE } from '../define/common.define';
import { parseStatItems, normalizeQmCategory } from '../utils/statInfo.utils';
import { StatCategory, StatInfo } from '../components/organisms/statWindow';
import { useViewModeStore } from './view-mode';
import { useMyInfoStore } from '@/config/stores/my-info';
import { useRegisteredExternalResourceTypes } from '@/dashboard/shared/composables/useRegisteredExternalResourceTypes';
import { getMaxgaugeStatCategories } from '@/dashboard/shared/utils/externalResource.utils';

type ParentCategory = string;
type ChildCategory = string;

export const getStatInfoList = (
  statInfoListByCategory: Map<ParentCategory, Map<ChildCategory, StatInfo[]>>,
  { parentCategory, childCategory }: StatCategory,
): StatInfo[] | undefined => {
  const normalizedChildCategory = normalizeQmCategory(parentCategory, childCategory);
  return statInfoListByCategory.get(parentCategory)?.get(normalizedChildCategory);
};

export const useStatInfoStore = defineStore('statInfo', () => {
  const { viewMode } = storeToRefs(useViewModeStore());

  const { myInfo: userInfo } = storeToRefs(useMyInfoStore());
  const { registeredExternalResourceTypes } = useRegisteredExternalResourceTypes(
    computed(() => viewMode.value === VIEW_MODE.MAXGAUGE),
  );

  const statCategoriesByViewMode = computed(() => ({
    [VIEW_MODE.MAXGAUGE]: getMaxgaugeStatCategories(registeredExternalResourceTypes.value),
    [VIEW_MODE.EXEMONE]: [],
    [VIEW_MODE.INTERMAX]: [],
    [VIEW_MODE.CLOUDMOA]: [],
  }));

  // Vue Query: Stat 카테고리 목록 조회
  const categoryParams = computed(() => ({
    categories: statCategoriesByViewMode.value[viewMode.value] ?? [],
  }));

  const { data: dataStatCategoriesFormList, isPending: isCategoryListLoading } =
    useGetV7DatasStatCommonCategories(categoryParams, {
      query: {
        select: (response) => response.data?.data,
      },
    });

  // Vue Query: Stat 목록 조회
  const currentUserId = computed(() => userInfo.value?.userId ?? -1);

  const statParams = computed(() => ({
    userId: currentUserId.value,
  }));

  const { data: statsData, isPending: isStatsDataLoading } = useGetV7DatasStat(statParams, {
    query: {
      enabled: computed(() => currentUserId.value > 0),
      select: (response) => response.data?.data?.at(0),
    },
  });

  const isLoading = computed(() => isCategoryListLoading.value || isStatsDataLoading.value);
  const statInfoList = computed(() => statsData.value?.dataIds ?? []);

  // 두 데이터 소스가 모두 있을 때 파싱 결과를 computed로 캐싱
  const parsedStatInfo = computed(() => {
    if (!statsData.value || !dataStatCategoriesFormList.value) {
      return null;
    }
    return parseStatItems(dataStatCategoriesFormList.value, statsData.value);
  });

  const statInfo = computed(() => parsedStatInfo.value?.info);
  const statInfoByCategoryAndStatId = computed(() => parsedStatInfo.value?.infoByStatIdAndCategory);
  const statInfoListByCategory = computed(() => parsedStatInfo.value?.infoByCategoryInfo);

  const hasStatInfo = computed(() => parsedStatInfo.value !== null);

  const getStatInfo = ({
    childCategory,
    statId,
  }: {
    childCategory?: string;
    statId: string;
  }): StatInfo | undefined => {
    if (!childCategory || statId.startsWith('qm_')) {
      return statInfo.value?.get(statId);
    }

    return statInfoByCategoryAndStatId.value?.get(childCategory)?.get(statId);
  };

  const getStatDisplayNameByDataId = (statId: string): string => {
    const stat = statInfo.value?.get(statId);
    return stat?.name ?? '';
  };

  return {
    isLoading,

    getStatInfo,
    getStatDisplayNameByDataId,

    statInfoListByCategory,
    statInfoByCategoryAndStatId,
    hasStatInfo,
    statInfoList,
    dataStatCategoriesFormList,
    statCategoriesByViewMode,
  };
});
